<template>
  <div>
    <!-- Table Container Card -->
    <b-card no-body class="mb-0">
      <div class="m-2">
        <!-- Filtros (una sola línea) -->
        <b-row align-v="end">

          <!-- Per Page -->
          <b-col
              cols="auto"
              class="d-flex align-items-center"
          >
            <label class="mb-0 mr-50">Mostrar</label>
            <v-select
                v-model="perPage"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="perPageOptions"
                :clearable="false"
                class="per-page-selector d-inline-block"
            />
          </b-col>

          <!-- Estado -->
          <b-col>
            <label>Estado</label>
            <v-select
              v-model="statusFilter"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="statusOptions"
              :reduce="(o) => o.value"
              label="label"
              :clearable="false"
              placeholder="Todos"
            />
          </b-col>
          <b-col>
            <label>Prioridad</label>
            <v-select
              v-model="priorityFilter"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="priorityOptions"
              :reduce="(o) => o.value"
              label="label"
              :clearable="false"
              placeholder="Todos"
            />
          </b-col>
          <b-col>
            <label>Rango de fechas</label>
            <div class="d-flex align-items-center">
              <flat-pickr
                v-model="rangeDate"
                class="form-control"
                placeholder="Rango de fechas"
                :config="{
                  mode: 'range',
                  showMonths: 2,
                  format: 'd-m-Y',
                  altFormat: 'd-m-Y',
                  altInput: true,
                  locale: Spanish,
                }"
                @on-change="onRangeChange"
              />
              <b-button
                v-if="rangeDate"
                variant="outline-danger"
                class="btn-icon ml-1"
                @click="clearRange"
              >
                <feather-icon icon="XIcon" />
              </b-button>
            </div>
          </b-col>
          <!-- Search -->
          <b-col>
            <label>Buscar</label>
            <b-form-input
              v-model="searchQuery"
              placeholder="Buscar..."
            />
          </b-col>
          <b-col
            cols="auto"
            class="d-flex align-items-end"
          >
            <b-button variant="primary" @click="editPedido('new')">
              <span class="text-nowrap">Crear Pedido</span>
            </b-button>
          </b-col>
        </b-row>
      </div>

      <b-table
        ref="refPedidosListTable"
        class="position-relative"
        :items="fetchPedidos"
        responsive
        :fields="tableColumns"
        primary-key="id"
        :sort-by.sync="sortBy"
        show-empty
        empty-text="No se encontraron registros coincidentes"
        :sort-desc.sync="isSortDirDesc"
        :busy="!fetchPedidos"
      >
        <!-- Table Loading -->
        <template #table-busy>
          <div class="text-center text-primary my-2">
            <b-spinner class="align-middle" small />
            <strong class="ml-1">Cargando datos...</strong>
          </div>
        </template>

        <!-- Column: Priority -->
        <template #cell(is_urgent)="data">
          <b-badge :variant="data.item.is_urgent ? 'danger' : 'secondary'">
            {{ data.item.is_urgent ? "URGENTE" : "NORMAL" }}
          </b-badge>
        </template>

        <!-- Column: Codigo -->
        <template #cell(codigo)="data">
          <b-link
            class="font-weight-bold d-block text-nowrap"
            @click="editPedido(data.item.id)"
          >
            #{{ data.item.codigo }}
          </b-link>
        </template>

        <!-- Column: User Fecha -->
        <template #cell(fecha)="data">
          <span>{{ formatDate(data.item.fecha) }}</span>
        </template>

        <!-- Column: User Creado -->
        <template #cell(fk_user_creado)="data">
          <span>{{ data.item.user_creado_name }}</span>
        </template>

        <!-- Column: Estado -->
        <template #cell(estado)="data">
          <b-badge
            pill
            :variant="`light-${resolveStatusVariant(data.item.estado)}`"
            class="text-capitalize"
          >
            {{ resolveStatusText(data.item.estado) }}
          </b-badge>
        </template>

        <!-- Column: Actions -->
        <template #cell(actions)="data">
          <b-row>
            <!-- Editar -->
            <b-button
              :id="`edit-${data.item.id}`"
              @click="editPedido(data.item.id)"
              variant="success"
              class="btn-icon px-1 mr-1"
            >
              <feather-icon icon="EditIcon" />
            </b-button>
            <b-tooltip
              :target="`edit-${data.item.id}`"
              triggers="hover"
              placement="bottom"
              boundary="window"
            >
              <b>Editar</b>
            </b-tooltip>

            <b-dropdown
              :id="`more-${data.item.id}`"
              dropup
              variant="info"
              size="sm"
              no-caret
              style="height: 36px"
            >
              <template #button-content>
                <feather-icon icon="MoreVerticalIcon" />
              </template>

              <!-- Send -->
              <b-dropdown-item
                v-if="data.item.estado === 'borrador'"
                :id="`send-${data.item.id}`"
                @click="sendPedido(data.item.id)"
              >
                <feather-icon
                  icon="SendIcon"
                  class="mr-1"
                />Enviar</b-dropdown-item
              >

              <!-- Duplicate -->
              <b-dropdown-item
                :id="`duplicate-${data.item.id}`"
                @click="duplicatePedido(data.item.id)"
                ><feather-icon
                  icon="CopyIcon"
                  class="mr-1"
                />Duplicar</b-dropdown-item
              >

              <!-- Imprimir -->
              <b-dropdown-item
                :id="`imprimir-${data.item.id}`"
                @click="imprimirPedido(data.item.id)"
              >
                <feather-icon
                  icon="PrinterIcon"
                  class="mr-1"
                />Imprimir</b-dropdown-item
              >

              <!-- Eliminar -->
              <b-dropdown-item
                v-if="data.item.estado === 'borrador'"
                :id="`delete-${data.item.id}`"
                @click="
                  deleteModal = true;
                  selectedItem = data.item;
                "
                variant="danger"
              >
                <feather-icon icon="TrashIcon" class="mr-1" />
                Eliminar</b-dropdown-item
              >
            </b-dropdown>

            <b-tooltip
              :target="`more-${data.item.id}`"
              triggers="hover"
              placement="bottom"
              boundary="window"
            >
              <b>Mas opciones</b>
            </b-tooltip>
          </b-row>
        </template>
      </b-table>
      <div class="mx-2 mb-2">
        <b-row>
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-start"
          >
            <span class="text-muted"
              >Mostrando {{ dataMeta.from }} a {{ dataMeta.to }} de
              {{ dataMeta.of }} entradas</span
            >
          </b-col>
          <!-- Pagination -->
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-end"
          >
            <b-pagination
              v-model="currentPage"
              :total-rows="totalPedidos"
              :per-page="perPage"
              first-number
              last-number
              class="mb-0 mt-1 mt-sm-0"
              prev-class="prev-item"
              next-class="next-item"
            >
              <template #prev-text>
                <feather-icon icon="ChevronLeftIcon" size="18" />
              </template>
              <template #next-text>
                <feather-icon icon="ChevronRightIcon" size="18" />
              </template>
            </b-pagination>
          </b-col>
        </b-row>
      </div>
    </b-card>

    <ConfirmationModal
      :id="`confirmation-${selectedItem.id}`"
      :show="deleteModal"
      message="Esta acción no se puede revertir y se perderá el pedido."
      title="¿Está seguro de que desea eliminar el pedido?"
      @action="deletePedido(selectedItem.id)"
      @close="deleteModal = false"
    />
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BFormInput,
  BButton,
  BTable,
  BLink,
  BBadge,
  BPagination,
  BSpinner,
  BTooltip,
  BDropdown,
  BDropdownItem,
} from "bootstrap-vue";
import vSelect from "vue-select";
import flatPickr from "vue-flatpickr-component";
import { Spanish } from "flatpickr/dist/l10n/es";
import moment from "moment";
import store from "@/store";
import { onUnmounted } from "@vue/composition-api";
import usePedidosList from "./usePedidosList";
import pedidosStoreModule from "./pedidosStoreModule";
import ConfirmationModal from "@/components/ConfirmationModal.vue";

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BFormInput,
    BButton,
    BTable,
    BLink,
    BBadge,
    BPagination,
    BSpinner,
    BTooltip,
    BDropdown,
    BDropdownItem,

    vSelect,
    flatPickr,
    ConfirmationModal,
  },
  data() {
    return {
      deleteModal: false,
      selectedItem: {},
      Spanish,
      rangeDate: null,
      statusOptions: [
        { label: "Todos", value: "" },
        { label: "Borrador", value: "borrador" },
        { label: "Pendiente", value: "pendiente" },
        { label: "Aceptado", value: "aceptado" },
        { label: "Entregado", value: "entregado" },
        { label: "Cancelado", value: "cancelado" },
      ],
      priorityOptions: [
        { label: "Todos", value: "" },
        { label: "Normal", value: "0" },
        { label: "Urgente", value: "1" },
      ],
    };
  },
  methods: {
    // flatpickr range: filtramos al completar el rango (2 fechas) o al limpiarlo (0).
    onRangeChange(selectedDates) {
      if (selectedDates.length === 2) {
        this.fechaDesde = moment(selectedDates[0]).format("YYYY-MM-DD");
        this.fechaHasta = moment(selectedDates[1]).format("YYYY-MM-DD");
      } else if (selectedDates.length === 0) {
        this.fechaDesde = null;
        this.fechaHasta = null;
      }
    },
    clearRange() {
      this.rangeDate = null;
      this.fechaDesde = null;
      this.fechaHasta = null;
    },
    sendPedido(id) {
      const estado = "pendiente";
      this.$store
        .dispatch("app-pedidos/changeStatus", { id, estado })
        .then(() => {
          this.refetchData();
        });
    },
    imprimirPedido(id) {
      this.$store
        .dispatch("app-pedidos/printPedido", id)
        .then((response) => {
          window.open(response.route, "_blank");
        })
        .catch((error) => {
          console.error("Error printing the pedido:", error);
        });
    },
    duplicatePedido(id) {
      this.$store.dispatch("app-pedidos/duplicatePedido", id).then(() => {
        this.refetchData();
      });
    },
    editPedido(id) {
      this.$router.push({
        name: "pedido-edit",
        params: {
          id,
        },
      });
    },
    deletePedido(id) {
      this.$store.dispatch("app-pedidos/deletePedido", id).then(() => {
        this.deleteModal = false;
        this.refetchData();
      });
    },
  },
  mounted() {
    // Restaurar el rango visible del picker si la URL traía fechas (useQuerySync ya
    // rellenó fechaDesde/fechaHasta).
    if (this.fechaDesde && this.fechaHasta) {
      this.rangeDate = [this.fechaDesde, this.fechaHasta];
    }
  },
  setup() {
    const PEDIDOS_APP_STORE_MODULE_NAME = "app-pedidos";

    // Register module
    if (!store.hasModule(PEDIDOS_APP_STORE_MODULE_NAME)) {
      store.registerModule(PEDIDOS_APP_STORE_MODULE_NAME, pedidosStoreModule);
    }

    // UnRegister on leave
    onUnmounted(() => {
      if (store.hasModule(PEDIDOS_APP_STORE_MODULE_NAME))
        store.unregisterModule(PEDIDOS_APP_STORE_MODULE_NAME);
    });

    const {
      fetchPedidos,
      tableColumns,
      perPage,
      currentPage,
      totalPedidos,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refPedidosListTable,
      refetchData,

      // UI
      resolveStatusVariant,
      resolveStatusText,
      parseDate,

      // Extra Filters
      roleFilter,
      planFilter,
      statusFilter,
      priorityFilter,
      fechaDesde,
      fechaHasta,
    } = usePedidosList();

    return {
      fetchPedidos,
      tableColumns,
      perPage,
      currentPage,
      totalPedidos,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refPedidosListTable,
      refetchData,

      // UI
      resolveStatusVariant,
      resolveStatusText,
      parseDate,

      // Extra Filters
      roleFilter,
      planFilter,
      statusFilter,
      priorityFilter,
      fechaDesde,
      fechaHasta,
    };
  },
};
</script>

<style lang="scss" scoped>
.per-page-selector {
  width: 90px;
}
</style>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
@import "@core/scss/vue/libs/vue-flatpicker.scss";
</style>
